import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
// Read model for a scope's accountability chains (ADR-026 §C) — the set of
// responsibility definitions AT this scope's level + each one's ordered chain
// (member ids, lead→backup). LEVEL-AGNOSTIC: a zone / cluster / team / site /
// warehouse all read the same shape. This carries only the ordered ids (mirrors the
// Workbench fixture, where `chainFor()` returns ids); the client names them through
// the person lookup's rows of given ids (ADR-078 §6).
// One responsibility definition + its ordered chain at the scope.
export const ScopeChainSchema = z
    .object({
    responsibility_id: z.number().int(),
    name: z.string().max(32),
    is_required: z.boolean(),
    // Ordered lead→backup user ids; index 0 = lead. Empty = unassigned.
    member_ids: z.array(z.string().max(16)),
})
    .strict();
// ── GET …/<scope>/<id>/responsibilities — chains at the scope ───────────────
// `row_version` — the PLACE's stamp, read with the chains (ADR-084 §3 · §4: a chain is a set beneath its place, and
// the editor opens on this read — its save carries this stamp back); null at a level with no stamped row (a
// warehouse, a pool).
export const ScopeChainsResponseSchema = z
    .object({ items: z.array(ScopeChainSchema), row_version: RowVersionSchema.nullable() })
    .strict();
// ── Backing-impact preview (ADR-052 §5) — the shared shape behind every
// un-backing mutation's proactive surfacing: the chains a proposed change would
// leave UNBACKED, each flagged `clearable` (may the acting operator drop it?).
// Reused by the coverage editor (scope change) + the cluster reparent (zone change).
// `holder_id` = the assignee who'd be orphaned; `scope_label` = the place's display
// name (zone/cluster) for the message. `blocked` = any orphaned chain is NOT clearable
// (→ the mutation is refused); otherwise the clearable set is the confirm list.
export const OrphanedChainSchema = z
    .object({
    holder_id: z.string().max(16),
    responsibility_id: z.number().int(),
    responsibility_name: z.string().max(64),
    level: z.string().max(16),
    scope_id: z.string().max(20),
    scope_label: z.string().max(64),
    clearable: z.boolean(),
})
    .strict();
export const BackingImpactSchema = z
    .object({
    orphaned: z.array(OrphanedChainSchema),
    blocked: z.boolean(),
})
    .strict();
